import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Card,
  Table,
} from "react-bootstrap";
import { CircularProgress } from "@mui/material";
import { logActivity } from "../../../utils/LogActivity";
import { AuthContext } from "../../../Context/AuthContext";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
const ViewBill = () => {
  const [inputValue, setInputValue] = useState("");
  const [searchType, setSearchType] = useState("bill");
  const [billDetails, setBillDetails] = useState(null);
  const [repayments, setRepayments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Loader starts as true
  const Api = process.env.REACT_APP_API;
  const { userId } = useContext(AuthContext);

  useEffect(() => {
    setLoading(false);
  }, []);

  // Handle bill search
  const handleBillSearch = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections/bill-number`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...config.headers,
        },
        body: JSON.stringify({ billNumber: inputValue }),
      });

      if (!response.ok) {
        throw new Error("Bill not found or an error occurred.");
      }

      const data = await response.json();
      setBillDetails(data);
      setRepayments([]); // Clear previous loan search results
      await logActivity(userId, "Viewed State", "State Page", Api);
      setError(null);
    } catch (err) {
      setBillDetails(null);
      setError(err.message);
    }
    setLoading(false);
  };

  // Handle loan search
  const handleLoanSearch = async () => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections/loan-number`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...config.headers,
        },
        body: JSON.stringify({ loanNumber: inputValue }),
      });

      if (!response.ok) {
        throw new Error("Loan not found or an error occurred.");
      }

      const data = await response.json();
      setRepayments(data);
      setBillDetails(null); // Clear previous bill search results
      await logActivity(userId, "Viewed View loan Bill", "View Bill Page", Api);
      setError(null);
    } catch (err) {
      setRepayments([]);
      setError(err.message);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    if (searchType === "bill") {
      handleBillSearch();
    } else if (searchType === "loan") {
      handleLoanSearch();
    }
  };

  const formatDate = (date) => {
    if (!date) return "No payment date available";
    const parsedDate = new Date(date);
    return isNaN(parsedDate) ? "Invalid Date" : parsedDate.toLocaleString();
  };

  return (
    <Container>
      <Preloader loading={loading} />

      <div className="page-header">
        <h3 className="page-title"> View Bill or Loan Repayments </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              View Bill or Loan Repayments
            </li>
          </ol>
        </nav>
      </div>

      {loading ? (
        <div className="loading-container">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <Row className="mb-4 align-items-end">
                  <Col md={4}>
                    <Form.Group controlId="formSearchType">
                      <Form.Label>Select Search Type</Form.Label>
                      <div className="input-container">
                        <Form.Control
                          as="select"
                          value={searchType}
                          onChange={(e) => setSearchType(e.target.value)}
                        >
                          <option value="bill">Bill Number</option>
                          <option value="loan">Loan Number</option>
                        </Form.Control>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formInputValue">
                      <Form.Label>
                        {searchType === "bill"
                          ? "Enter Bill Number"
                          : "Enter Loan Number"}
                      </Form.Label>
                      <div className="input-container">
                        <Form.Control
                          type="text"
                          placeholder={
                            searchType === "bill"
                              ? "Enter Bill Number"
                              : "Enter Loan Number"
                          }
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-md"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                  </Col>
                </Row>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Row className="mb-4">
              <Col>
                <Alert variant="danger">{error}</Alert>
              </Col>
            </Row>
          )}

          {/* Display Bill Details */}
          {billDetails && searchType === "bill" && (
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <Card.Title>Bill Details</Card.Title>
                    <Card.Text>
                      <strong>Loan ID:</strong> {billDetails.loanId.loanNo}
                    </Card.Text>
                    <Card.Text>
                      <strong>Sub Loan ID:</strong> {billDetails.subLoanId}
                    </Card.Text>
                    <Card.Text>
                      <strong>Amount Paid:</strong> {billDetails.amountPaid}
                    </Card.Text>
                    <Card.Text>
                      <strong>Interest Paid:</strong> {billDetails.interestPaid}
                    </Card.Text>
                    <Card.Text>
                      <strong>Bill Number:</strong> {billDetails.billNumber}
                    </Card.Text>
                    <Card.Text>
                      <strong>Payment Date:</strong>{" "}
                      {formatDate(billDetails.paymentDate)}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Display Repayments */}
          {repayments.length > 0 && searchType === "loan" && (
            <Row className="mt-4">
              <Col>
                <div className="page-header">
                  <h3 className="page-title"> Repayment History </h3>
                  <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                      <li className="breadcrumb-item">
                        <a
                          href="!#"
                          onClick={(event) => event.preventDefault()}
                        >
                          VFC
                        </a>
                      </li>
                      <li
                        className="breadcrumb-item active"
                        aria-current="page"
                      >
                        Repayment History
                      </li>
                    </ol>
                  </nav>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="row icons-list">
                      <div className="Table_data">
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Bill Number</th>
                              <th>Amount Paid</th>
                              <th>Interest Paid</th>
                              <th>Payment Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {repayments.map((repayment, index) => (
                              <tr key={repayment._id}>
                                <td>{index + 1}</td>
                                <td>{repayment.billNumber}</td>
                                <td>{repayment.amountPaid}</td>
                                <td>{repayment.interestPaid}</td>
                                <td>{formatDate(repayment.paymentDate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Show no data message */}
          {!billDetails && repayments.length === 0 && !error && (
            <Row className="mb-4">
              <Col>
                <p>No details found.</p>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default ViewBill;
